// react
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// mux
import MuxPlayer from '@mux/mux-player-react';

// models
import {
  ClientDetails,
  FeesDetails,
  GdprDetails,
  PartnerDetails,
  Referral,
  ReferralFormType,
} from '@karehero/models';

// components
import {
  Button,
  Card,
  Container,
  SupportingText,
  Title,
} from '@/components/atoms';
import {
  CareReferralInfo,
  FeesDescription,
  GdprConsent,
  SjpClientForm,
  SjpPartnerForm,
} from '@/components/organisms';
import { ReferralProcess } from '@/components/molecules';

// hooks
import { SetIsValid, useValidator } from '@/hooks';

// assets
// @transform-path ../../../../../../src/assets/referral/header.jpg
import headerImage from '@/assets/referral/header.jpg';

// validators
const isClientValid = (c: any): boolean => {
  return Object.keys(c).length === 6;
};

const isPartnerValid = (c: any): boolean => {
  return Object.keys(c).length === 6;
};

const isGdprValid = (c: any): boolean => {
  return JSON.parse(c?.gdprSelections || '[]').length === 2;
};

const isFeesValid = (c: any): boolean => {
  return c?.feeSelection !== undefined;
};

/**
 * CareConciergeReferral renders the account settings page.
 */
export const CareConciergeReferral = ({
  onRefer,
  setIsValid,
  videoPlaybackID,
  videoThumbnailTime = 0.0,
}: CareConciergeReferralProps) => {
  // states
  const [client, setClient] = useState<Partial<ClientDetails>>({});
  const [partner, setPartner] = useState<Partial<PartnerDetails>>({});
  const [gdpr, setGdpr] = useState<Partial<GdprDetails>>({});
  const [fees, setFees] = useState<Partial<FeesDetails>>({});

  // hooks
  const { createSetIsValid, isValid: isValidationValid } =
    useValidator(setIsValid);

  // memos
  const referral: Referral = useMemo(() => {
    return {
      referrer: {
        firstName: partner.firstName,
        lastName: partner.lastName,
        email: partner.email,
        contactNumber: partner.phoneNumber,
        partnerCode: partner.partnerCode,
        referLtcCode: partner.ltcLicense,
        gdprSelections: gdpr.gdprSelections,
      },
      referral: {
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email,
        phone: client.phoneNumber,
        discountCode: fees.discountCode,
        feePaid: fees.feeSelection,
        personReceivingCare: client.nameOfPersonReceivingCare,
        careSituationContext: client.contextAboutSituation,
      },
    };
  }, [client, partner, gdpr, fees]);

  const isValid = useMemo(
    () =>
      isClientValid(client) &&
      isPartnerValid(partner) &&
      isGdprValid(gdpr) &&
      isFeesValid(fees),
    [client, partner, gdpr, fees],
  );

  return (
    <Wrapper>
      <Header>
        <img src={headerImage} alt='header' />
        <InfoWrapper>
          <Container size='xl'>
            <CareReferralInfo
              onRefer={() => {
                document
                  .querySelector('[id="sjp-client-form"]')
                  ?.scrollIntoView();
              }}
            />
          </Container>
        </InfoWrapper>
      </Header>
      <Page>
        {videoPlaybackID && (
          <Container size='sm'>
            <Content>
              <StyledMuxPlayer
                playbackId={videoPlaybackID}
                thumbnailTime={videoThumbnailTime}
              />
            </Content>
          </Container>
        )}
        <Container size='xl'>
          <ContentWrapper>
            <Content>
              <Title isNoMargin>How your referral works</Title>
              <Description>
                See Frequently Asked Questions from other SJP Partners{' '}
                <StyledInternalLink
                  target='_blank'
                  rel='noopener noreferrer'
                  to='/docs/sjp/KareHero-FAQ.pdf'
                >
                  here
                </StyledInternalLink>
                . Please contact us at sjp@karehero.com for further questions.
              </Description>
              <ReferralProcess formType={ReferralFormType.SjpIFA} />
            </Content>
            <div id='sjp-client-form'>
              <SjpClientForm
                formType={ReferralFormType.SjpIFA}
                value={client}
                setValue={setClient}
                setIsValid={createSetIsValid('client')}
              />
            </div>
            <SjpPartnerForm
              value={partner}
              setValue={setPartner}
              setIsValid={createSetIsValid('partner')}
            />
            <FeesDescription
              value={fees}
              setValue={setFees}
              formType={ReferralFormType.SjpIFA}
            />
            <GdprConsent
              value={gdpr}
              setValue={setGdpr}
              formType={ReferralFormType.SjpIFA}
            />
            <Content>
              <Card color='info' isBordered={false} isPaddingSm>
                <StyledSupportingText
                  text={`Once you click 'Submit', head to your SJP email inbox for your personal invite to track your client's progress via your Partner Portal`}
                  size='md'
                />
              </Card>
              <div>
                <Button
                  ariaLabel={'refer-now'}
                  onPress={() => onRefer(referral)}
                  isDisabled={!isValid || !isValidationValid}
                  size='sm'
                  isRectangle
                >
                  Submit
                </Button>
              </div>
            </Content>
          </ContentWrapper>
        </Container>
      </Page>
    </Wrapper>
  );
};

// styles
const Wrapper = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 320px;
  gap: ${(props) => props.theme.spacing[36]};
  padding-bottom: ${(props) => props.theme.spacing[32]};
`;

const InfoWrapper = styled.div`
  padding: ${(props) => props.theme.spacing[16]};
  ${(props) =>
    props.theme.breakpoint.sm(css`
      padding: ${(props) => props.theme.spacing[24]};
    `)}
  ${(props) =>
    props.theme.breakpoint.md(css`
      padding: ${(props) => props.theme.spacing[48]};
    `)}
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
`;

const Description = styled.div`
  text-align: left;
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const Header = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  & > img {
    z-index: -1;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    user-drag: none;
    -webkit-user-drag: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
`;

const StyledInternalLink = styled(Link)`
  color: ${(props) => props.theme.color.cards.red.tertiary};
  font: ${(props) => props.theme.typography.body.sm.medium};
  text-decoration: underline;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
`;

const StyledSupportingText = styled(SupportingText)`
  width: 100%;
  color: ${(props) => props.theme.color.neutral[100]};
`;

const StyledMuxPlayer = styled(MuxPlayer)`
  border-radius: 8px;
  overflow: hidden;
  ${(props) =>
    props.theme.breakpoint.md(css`
      margin: auto;
      max-width: 100%;
    `)}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
`;

// types
interface CareConciergeReferralProps {
  onRefer: (referral: Referral) => void;
  setIsValid?: SetIsValid;
  videoPlaybackID?: string;
  videoThumbnailTime?: number;
}

CareConciergeReferral.displayName = 'CareConciergeReferral';

export default CareConciergeReferral;
